/**
 * LPK Admin — Design System
 * Style: Dark sidebar (slate-900) + clean white content + modern UI/UX
 * Inspired by Linear / Vercel / Supabase dashboard aesthetic
 */
import { cn } from '../../lib/utils.js'
import { RiCloseLine, RiInboxLine } from 'react-icons/ri'

/* ──── Button ─────────────────────────────────────────── */

export function Btn({ variant = 'primary', size = 'md', className, children, ...props }) {
  const base = [
    'inline-flex items-center justify-center gap-1.5 font-semibold transition-all duration-150 active:scale-95',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
  ].join(' ')

  const sizes = {
    sm:  'h-7  px-3  text-xs  rounded-lg',
    md:  'h-9  px-4  text-sm  rounded-lg',
    lg:  'h-11 px-5  text-sm  rounded-xl',
  }

  const variants = {
    primary:   'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 shadow-sm hover:shadow-md',
    secondary: 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 shadow-sm',
    ghost:     'text-gray-500 hover:bg-gray-100 hover:text-gray-900',
    danger:    'border border-red-200 bg-white text-red-600 hover:bg-red-50 hover:border-red-300',
    dark:      'bg-slate-900 text-white hover:bg-slate-800 active:bg-black shadow-sm',
  }

  return (
    <button className={cn(base, sizes[size], variants[variant], className)} {...props}>
      {children}
    </button>
  )
}

/* ──── Input ─────────────────────────────────────────── */

export function Input({ className, ...props }) {
  return (
    <input className={cn(
      'h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 shadow-sm',
      'placeholder:text-gray-400 outline-none',
      'focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-shadow',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      className
    )} {...props} />
  )
}

export function Textarea({ className, ...props }) {
  return (
    <textarea className={cn(
      'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm',
      'placeholder:text-gray-400 outline-none resize-none',
      'focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-shadow',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      className
    )} {...props} />
  )
}

export function Select({ className, ...props }) {
  return (
    <select className={cn(
      'h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 shadow-sm',
      'outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-shadow',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      className
    )} {...props} />
  )
}

export function Label({ className, children, ...props }) {
  return (
    <label className={cn('block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide', className)} {...props}>
      {children}
    </label>
  )
}

export function Field({ label, req, children }) {
  return (
    <div>
      {label && (
        <Label>
          {label}{req && <span className="ml-0.5 text-red-500" aria-hidden>*</span>}
        </Label>
      )}
      {children}
    </div>
  )
}

/* ──── Toggle / Switch ─────────────────────────────────────── */

export function Toggle({ checked, onChange, disabled, size = 'md' }) {
  const track = size === 'sm' ? 'h-4 w-7' : 'h-5 w-9'
  const thumb = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'
  const shift = size === 'sm' ? 'translate-x-3' : 'translate-x-4'
  return (
    <button
      type="button" role="switch" aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange && onChange(!checked)}
      className={cn(
        `relative inline-flex ${track} flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200`,
        checked ? 'bg-blue-600' : 'bg-gray-200',
        disabled && 'cursor-not-allowed opacity-50'
      )}
    >
      <span className={cn(
        `pointer-events-none inline-block ${thumb} rounded-full bg-white shadow-md transform ring-0 transition-transform duration-200`,
        checked ? shift : 'translate-x-0'
      )} />
    </button>
  )
}

/* ──── Badge ─────────────────────────────────────────────── */

export function Badge({ children, variant = 'default', className }) {
  const variants = {
    default:  'bg-gray-100 text-gray-600',
    active:   'bg-emerald-100 text-emerald-700',
    inactive: 'bg-gray-100 text-gray-500',
    warning:  'bg-amber-100 text-amber-700',
    info:     'bg-blue-100 text-blue-700',
    coral:    'bg-red-800 text-white',
    forest:   'bg-green-900 text-white',
  }
  return (
    <span className={cn(
      'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
      variants[variant], className
    )}>
      {children}
    </span>
  )
}

/* ──── Modal ───────────────────────────────────────────────── */

export function Modal({ children, onClose, title, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
        <div
          className={cn(
            'relative w-full rounded-2xl border border-gray-200 bg-white shadow-2xl',
            wide ? 'max-w-3xl' : 'max-w-lg'
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            >
              <RiCloseLine className="text-lg" />
            </button>
          </div>
          <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

/* ──── Page header ───────────────────────────────────────── */

export function PageHeader({ title, description, action }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
        {description && <p className="mt-1 text-sm text-gray-500 max-w-2xl">{description}</p>}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  )
}

/* ──── Empty state ───────────────────────────────────────── */

export function EmptyState({ message, hint }) {
  return (
    <div className="py-16 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100">
        <RiInboxLine className="text-xl text-gray-300" />
      </div>
      <p className="text-sm font-medium text-gray-500">{message}</p>
      {hint && <p className="mt-1 text-sm text-gray-400">{hint}</p>}
    </div>
  )
}

/* ──── Pagination ───────────────────────────────────────── */

export function Pagination({ pagination, onPage }) {
  if (!pagination || pagination.last_page <= 1) return null
  return (
    <div className="flex items-center justify-between text-sm text-gray-500">
      <span>Halaman {pagination.current_page} dari {pagination.last_page}</span>
      <div className="flex gap-2">
        <Btn variant="secondary" size="sm" disabled={!pagination.prev_page_url} onClick={() => onPage(pagination.current_page - 1)}>
          ← Sebelumnya
        </Btn>
        <Btn variant="secondary" size="sm" disabled={!pagination.next_page_url} onClick={() => onPage(pagination.current_page + 1)}>
          Selanjutnya →
        </Btn>
      </div>
    </div>
  )
}

/* ──── Filter bar ───────────────────────────────────────── */

export function FilterBar({ children }) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
      {children}
    </div>
  )
}
